import { useForm } from "@tanstack/react-form";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { t } from "../../../i18n";
import { renameHouseholdSchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import { Badge, Button, Card, FieldMessage, Icon, Input, Spinner, Stack, Switch } from "../../ui";
import { DeleteHouseholdDialog } from "../dialogs/DeleteHouseholdDialog";

interface SettingsTabProps {
  householdId: string;
  name: string;
  role: string;
  ownerCoversInvoice: boolean;
}

export function SettingsTab({ householdId, name, role, ownerCoversInvoice }: SettingsTabProps) {
  const utils = trpc.useUtils();
  const rename = trpc.household.rename.useMutation({
    onSuccess: () => utils.household.get.invalidate({ groupId: householdId }),
  });
  const setOwnerCoversInvoice = trpc.household.setOwnerCoversInvoice.useMutation({
    onSuccess: () => utils.household.get.invalidate({ groupId: householdId }),
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isOwner = role === "owner";

  const renameForm = useForm({
    defaultValues: { name },
    validators: {
      onChange: renameHouseholdSchema,
      onSubmit: renameHouseholdSchema,
    },
    onSubmit: ({ value }) => {
      const trimmed = value.name.trim();
      if (trimmed === name.trim()) return;
      rename.mutate({ groupId: householdId, name: trimmed });
    },
  });

  // Server-side state can change while the user is on this page (another
  // tab renames the household). Sync the form back to truth when `name`
  // updates and the user isn't actively editing.
  useEffect(() => {
    renameForm.reset({ name });
  }, [name, renameForm]);

  return (
    <Stack gap={6}>
      <Card padding="md">
        <Stack gap={3}>
          <div>
            <h3 className="font-semibold text-ink-900 text-sm">
              {t.household.settings.nameSection.heading}
            </h3>
            <p className="mt-0.5 text-ink-500 text-xs">{t.household.settings.nameSection.hint}</p>
          </div>
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              renameForm.handleSubmit();
            }}
          >
            <renameForm.Field name="name">
              {(field) => (
                <div className="flex-1">
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    disabled={!isOwner}
                    invalid={!!fieldError(field)}
                  />
                  {fieldError(field) && (
                    <FieldMessage tone="error" className="mt-1">
                      {fieldError(field)}
                    </FieldMessage>
                  )}
                </div>
              )}
            </renameForm.Field>
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              disabled={!isOwner}
              isLoading={rename.isPending}
            >
              {t.common.save}
            </Button>
          </form>
        </Stack>
      </Card>
      <Card padding="md">
        <Stack gap={3}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-semibold text-ink-900 text-sm">
                {t.household.settings.ownerCoversInvoice.heading}
              </h3>
              <p className="mt-0.5 text-ink-500 text-xs">
                {t.household.settings.ownerCoversInvoice.hint}
              </p>
            </div>
            <Switch
              checked={ownerCoversInvoice}
              onCheckedChange={(value) =>
                setOwnerCoversInvoice.mutate({ groupId: householdId, value })
              }
              disabled={!isOwner || setOwnerCoversInvoice.isPending}
              aria-label={t.household.settings.ownerCoversInvoice.heading}
            />
          </div>
          {!isOwner && (
            <p className="text-ink-400 text-xs">
              {t.household.settings.ownerCoversInvoice.memberHint}
            </p>
          )}
        </Stack>
      </Card>
      <ConnectedCardsSection householdId={householdId} />
      {isOwner && (
        <Card
          padding="md"
          className="border-rose-200 bg-rose-50/30 dark:border-rose-800 dark:bg-rose-900/20"
        >
          <Stack gap={3}>
            <div>
              <h3 className="font-semibold text-rose-900 text-sm dark:text-rose-200">
                {t.household.settings.dangerZone.heading}
              </h3>
              <p className="mt-0.5 text-rose-700 text-xs dark:text-rose-300">
                {t.household.settings.dangerZone.hint}
              </p>
            </div>
            <Button
              variant="danger"
              iconLeft="trash"
              className="self-start"
              onClick={() => setDeleteOpen(true)}
            >
              {t.household.settings.dangerZone.deleteButton}
            </Button>
          </Stack>
        </Card>
      )}
      <DeleteHouseholdDialog
        householdId={householdId}
        name={name}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Connected cards — read-only list of cards shared with this household
// ---------------------------------------------------------------------------

function ConnectedCardsSection({ householdId }: { householdId: string }) {
  const me = trpc.auth.me.useQuery();
  const cards = trpc.cardConnections.listForGroup.useQuery({ groupId: householdId });

  return (
    <Card padding="md">
      <Stack gap={3}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-ink-900 text-sm">
              {t.household.settings.connectedCards.heading}
            </h3>
            <p className="mt-0.5 text-ink-500 text-xs leading-relaxed">
              {t.household.settings.connectedCards.hint}
            </p>
          </div>
          <Link
            to="/profile/cards"
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-accent-700 text-xs hover:underline dark:text-accent-300"
          >
            {t.household.settings.connectedCards.manageLink}
            <Icon name="arrow-right" size="xs" />
          </Link>
        </div>

        {cards.isPending && (
          <div className="flex items-center justify-center py-4">
            <Spinner />
          </div>
        )}

        {cards.isError && (
          <FieldMessage tone="error">
            {t.household.settings.connectedCards.loadFailed(cards.error.message)}
          </FieldMessage>
        )}

        {cards.data?.length === 0 && (
          <p className="text-ink-500 text-sm">{t.household.settings.connectedCards.empty}</p>
        )}

        {cards.data && cards.data.length > 0 && (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-md border border-ink-100 dark:divide-ink-200 dark:border-ink-200">
            {cards.data.map((card) => {
              const isMine = card.userId === me.data?.id;
              const sharedByLabel = isMine
                ? t.household.settings.connectedCards.sharedByYou
                : t.household.settings.connectedCards.sharedBy(card.ownerName ?? "—");
              const expiresAtMs = new Date(card.expiresAt).getTime();
              const isActive =
                card.status === "active" && expiresAtMs >= Date.now() && !card.needsRenewal;
              return (
                <div key={card.id} className="flex items-center gap-3 px-3 py-2.5">
                  {card.aspspLogo ? (
                    <img
                      src={card.aspspLogo}
                      alt=""
                      className="size-10 shrink-0 rounded border border-ink-200 bg-white object-contain p-1.5"
                    />
                  ) : (
                    <div className="flex size-10 shrink-0 items-center justify-center rounded border border-ink-200 bg-ink-50 text-ink-500">
                      <Icon name="credit-card" size="md" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-ink-900 text-sm">
                      {card.aspspName}
                    </div>
                    <div className="mt-0.5 truncate text-ink-500 text-xs">{sharedByLabel}</div>
                  </div>
                  {isActive ? (
                    <Badge tone="success" size="sm" iconLeft="check">
                      {t.cards.status.active}
                    </Badge>
                  ) : (
                    <Badge tone="warning" size="sm" iconLeft="alert-circle">
                      {t.cards.status.renewal}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Stack>
    </Card>
  );
}
